import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { EffectiveAccess, OktaVerifiedUser, OktaVerifyPayload } from "../services/authService";
import { resolveCatalogGroupsFromVerify } from "../utils/resolveCatalogGroups";
import { logout as logoutRemote } from "../services/authService";
import {
  getAuthUser,
  getEffectiveAccess,
  getMatchedGroups,
  isAuthenticated as sessionHasValidToken,
  setAuthSession,
} from "../services/authSessionService";

type AuthSessionState = {
  readonly user: OktaVerifiedUser | null;
  readonly effectiveAccess: EffectiveAccess | null;
  readonly matchedGroups: readonly string[];
};

function readSnapshotFromStorage(): AuthSessionState {
  if (!sessionHasValidToken()) {
    return { user: null, effectiveAccess: null, matchedGroups: [] };
  }
  return {
    user: getAuthUser(),
    effectiveAccess: getEffectiveAccess(),
    matchedGroups: getMatchedGroups(),
  };
}

function isShellReady(state: AuthSessionState): boolean {
  return (
    sessionHasValidToken() &&
    state.user != null &&
    state.effectiveAccess != null
  );
}

export type AuthSessionContextValue = AuthSessionState & {
  readonly adminRoles: readonly string[];
  readonly isShellAuthenticated: boolean;
  readonly setSessionFromVerify: (
    payload: OktaVerifyPayload,
    tokens: {
      readonly accessToken: string;
      readonly idToken: string;
      readonly expiresAt: number | null;
    },
  ) => void;
  readonly logout: () => Promise<void>;
};

const AuthSessionContext = createContext<AuthSessionContextValue | null>(null);

export const AuthSessionProvider = ({ children }: { readonly children: ReactNode }) => {
  const [state, setState] = useState<AuthSessionState>(() => readSnapshotFromStorage());

  const adminRoles = state.effectiveAccess?.admin_roles ?? [];

  const setSessionFromVerify = useCallback(
    (
      payload: OktaVerifyPayload,
      tokens: {
        readonly accessToken: string;
        readonly idToken: string;
        readonly expiresAt: number | null;
      },
    ) => {
      const matchedGroups = resolveCatalogGroupsFromVerify(payload.matched_groups, payload.groups);
      setAuthSession({
        accessToken: tokens.accessToken,
        idToken: tokens.idToken,
        expiresAt: tokens.expiresAt,
        user: payload.user,
        effectiveAccess: payload.effective_access,
        matchedGroups,
      });
      setState({
        user: payload.user,
        effectiveAccess: payload.effective_access,
        matchedGroups,
      });
    },
    [],
  );

  const logout = useCallback(async () => {
    await logoutRemote();
  }, []);

  const value = useMemo<AuthSessionContextValue>(
    () => ({
      ...state,
      adminRoles,
      isShellAuthenticated: isShellReady(state),
      setSessionFromVerify,
      logout,
    }),
    [state, adminRoles, setSessionFromVerify, logout],
  );

  return (
    <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>
  );
};

export const useAuthSession = (): AuthSessionContextValue => {
  const ctx = useContext(AuthSessionContext);
  if (!ctx) {
    throw new Error("useAuthSession must be used within AuthSessionProvider");
  }
  return ctx;
};
